import { z } from "zod";
import { Id } from "../common/ids.js";
import { LocalizedText } from "../common/localized-text.js";
import { Timestamp } from "../common/time.js";
import { defineRoute } from "../http/route.js";
import { ADMIN_ONLY_PERMISSIONS, TENANT_PERMISSIONS } from "../permissions.js";
const Email = z.email().max(254);
const Phone = z.string().regex(/^\+[1-9]\d{6,14}$/, "E.164 phone number");
const RoleIds = z.array(Id).min(1).max(20);
const TenantPermissionKey = z.enum(TENANT_PERMISSIONS).meta({ id: "TenantPermission" });
const Tenant = { auth: "user", surface: "tenant" };
export const Member = z
    .object({
    membershipId: Id,
    userId: Id,
    displayName: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    avatarFileId: Id.nullable(),
    status: z.enum(["invited", "active", "left"]),
    roleIds: z.array(Id),
    roles: z.array(z.string()).describe("Role keys"),
    departmentId: Id.nullable(),
    positionId: Id.nullable(),
    joinedAt: Timestamp.nullable(),
    leftAt: Timestamp.nullable(),
})
    .meta({ id: "Member" });
export const listMembers = defineRoute({
    ...Tenant,
    id: "identity.members.list",
    method: "GET",
    path: "/v1/enterprise/members",
    permission: "directory:read",
    marks: ["tenant"],
    summary: "Members of the active enterprise with their roles; people who left are listed with status left",
    responses: { 200: { description: "Members", schema: z.object({ items: z.array(Member) }) } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED"],
});
export const updateMember = defineRoute({
    ...Tenant,
    id: "identity.members.update",
    method: "PATCH",
    path: "/v1/enterprise/members/{membershipId}",
    permission: "member:manage",
    marks: ["tenant"],
    summary: "Replace a member's roles (effective at their next token refresh; the last admin keeps the admin role), department, or position",
    prd: ["IAM-4", "DIR-1"],
    request: {
        params: z.object({ membershipId: Id }),
        body: z
            .object({
            roleIds: RoleIds.optional(),
            departmentId: Id.nullable().optional(),
            positionId: Id.nullable().optional(),
        })
            .refine((value) => Object.values(value).some((field) => field !== undefined), {
            message: "Change at least one field",
        }),
    },
    responses: { 200: { description: "The member", schema: Member } },
    errors: [
        "AUTH_REQUIRED",
        "TENANT_REQUIRED",
        "PERMISSION_DENIED",
        "NOT_FOUND",
        "VALIDATION_FAILED",
        "INVALID_TRANSITION",
    ],
});
export const offboardMember = defineRoute({
    ...Tenant,
    id: "identity.members.offboard",
    method: "POST",
    path: "/v1/enterprise/members/{membershipId}/offboard",
    permission: "member:manage",
    marks: ["tenant", "idem"],
    summary: "Remove a member at once and name the colleague who takes over their contacts and conversations (done by the directory and messaging modules)",
    prd: ["DIR-3"],
    request: { params: z.object({ membershipId: Id }), body: z.object({ transferToMembershipId: Id }) },
    responses: { 200: { description: "The member, now left", schema: Member } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND", "INVALID_TRANSITION"],
});
export const Invitation = z
    .object({
    id: Id,
    targetEmail: z.string().nullable(),
    targetPhone: z.string().nullable(),
    roleIds: z.array(Id),
    status: z.enum(["pending", "accepted", "expired"]),
    expiresAt: Timestamp,
    createdAt: Timestamp,
})
    .meta({ id: "Invitation" });
export const createInvitation = defineRoute({
    ...Tenant,
    id: "identity.invitations.create",
    method: "POST",
    path: "/v1/enterprise/invitations",
    permission: "member:manage",
    marks: ["tenant", "idem"],
    summary: "Invite a person by email or phone with roles; the link is sent to them and expires in 7 days",
    request: {
        body: z
            .object({ email: Email.optional(), phone: Phone.optional(), roleIds: RoleIds })
            .refine((value) => (value.email === undefined) !== (value.phone === undefined), {
            message: "Give an email or a phone number, not both",
            path: ["email"],
        }),
    },
    responses: { 201: { description: "The invitation", schema: Invitation } },
    errors: [
        "AUTH_REQUIRED",
        "TENANT_REQUIRED",
        "PERMISSION_DENIED",
        "VALIDATION_FAILED",
        "INVALID_TRANSITION",
    ],
});
export const listInvitations = defineRoute({
    ...Tenant,
    id: "identity.invitations.list",
    method: "GET",
    path: "/v1/enterprise/invitations",
    permission: "member:manage",
    marks: ["tenant"],
    summary: "Invitations of the active enterprise, newest first",
    responses: { 200: { description: "Invitations", schema: z.object({ items: z.array(Invitation) }) } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED"],
});
export const revokeInvitation = defineRoute({
    ...Tenant,
    id: "identity.invitations.revoke",
    method: "DELETE",
    path: "/v1/enterprise/invitations/{invitationId}",
    permission: "member:manage",
    marks: ["tenant"],
    summary: "Withdraw a pending invitation; its link stops working",
    request: { params: z.object({ invitationId: Id }) },
    responses: { 204: { description: "Withdrawn" } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND"],
});
const InvitationToken = z.object({ token: z.string().min(20).max(100) });
export const viewInvitation = defineRoute({
    id: "identity.invitations.view",
    method: "GET",
    path: "/v1/invitations/{token}",
    surface: "tenant",
    auth: "none",
    permission: "none",
    marks: [],
    summary: "What an invitation link is for, before signing in: the enterprise and a masked target",
    request: { params: InvitationToken },
    responses: {
        200: {
            description: "The invitation",
            schema: z
                .object({
                enterpriseName: LocalizedText,
                target: z.string().describe("Masked email or phone the invitation was sent to"),
                status: z.enum(["pending", "accepted", "expired"]),
                expiresAt: Timestamp,
            })
                .meta({ id: "InvitationPreview" }),
        },
    },
    errors: ["NOT_FOUND"],
});
export const acceptInvitation = defineRoute({
    id: "identity.invitations.accept",
    method: "POST",
    path: "/v1/invitations/{token}/accept",
    surface: "tenant",
    auth: "user",
    permission: "none",
    marks: [],
    summary: "Join the enterprise; the signed-in user must own the invited email or phone. Switch to the enterprise next",
    request: { params: InvitationToken },
    responses: {
        200: { description: "Joined", schema: z.object({ enterpriseId: Id, membershipId: Id }) },
    },
    errors: ["AUTH_REQUIRED", "NOT_FOUND", "PERMISSION_DENIED", "INVALID_TRANSITION"],
});
export const listPermissions = defineRoute({
    ...Tenant,
    id: "identity.permissions.list",
    method: "GET",
    path: "/v1/permissions",
    permission: "directory:read",
    marks: ["tenant"],
    summary: "The permission catalogue custom roles are built from; `mfa` marks permissions that make a role need TOTP",
    prd: ["IAM-4"],
    responses: {
        200: {
            description: "Catalogue",
            schema: z.object({
                items: z.array(z.object({ key: TenantPermissionKey, module: z.string(), mfa: z.boolean() })),
            }),
        },
    },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED"],
});
export const Role = z
    .object({
    id: Id,
    key: z.string(),
    name: LocalizedText,
    isSystem: z.boolean(),
    permissions: z.array(TenantPermissionKey),
    requiresMfa: z.boolean(),
})
    .meta({ id: "Role" });
const RoleKey = z
    .string()
    .regex(/^[a-z][a-z0-9_]{1,39}$/, "Lower-case letters, digits, and underscores, starting with a letter");
/** What a custom role may hold: the catalogue without the admin's own permissions (card 128). */
const Permissions = z.array(TenantPermissionKey.exclude([...ADMIN_ONLY_PERMISSIONS])).min(1);
export const listRoles = defineRoute({
    ...Tenant,
    id: "identity.roles.list",
    method: "GET",
    path: "/v1/enterprise/roles",
    permission: "directory:read",
    marks: ["tenant"],
    summary: "System roles and the enterprise's custom roles with their permissions",
    prd: ["IAM-4"],
    responses: { 200: { description: "Roles", schema: z.object({ items: z.array(Role) }) } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED"],
});
export const createRole = defineRoute({
    ...Tenant,
    id: "identity.roles.create",
    method: "POST",
    path: "/v1/enterprise/roles",
    permission: "role:manage",
    marks: ["tenant"],
    summary: "Create a custom role from catalogue permissions",
    prd: ["IAM-4"],
    request: { body: z.object({ key: RoleKey, name: LocalizedText, permissions: Permissions }) },
    responses: { 201: { description: "The role", schema: Role } },
    errors: [
        "AUTH_REQUIRED",
        "TENANT_REQUIRED",
        "PERMISSION_DENIED",
        "VALIDATION_FAILED",
        "DUPLICATE_REGISTRATION",
    ],
});
export const updateRole = defineRoute({
    ...Tenant,
    id: "identity.roles.update",
    method: "PATCH",
    path: "/v1/enterprise/roles/{roleId}",
    permission: "role:manage",
    marks: ["tenant"],
    summary: "Rename a custom role or replace its permissions; members get them at their next token refresh",
    prd: ["IAM-4"],
    request: {
        params: z.object({ roleId: Id }),
        body: z.object({ name: LocalizedText.optional(), permissions: Permissions.optional() }),
    },
    responses: { 200: { description: "The role", schema: Role } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND", "INVALID_TRANSITION"],
});
export const deleteRole = defineRoute({
    ...Tenant,
    id: "identity.roles.delete",
    method: "DELETE",
    path: "/v1/enterprise/roles/{roleId}",
    permission: "role:manage",
    marks: ["tenant"],
    summary: "Delete a custom role nobody holds",
    prd: ["IAM-4"],
    request: { params: z.object({ roleId: Id }) },
    responses: { 204: { description: "Deleted" } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND", "INVALID_TRANSITION"],
});
export const closeEnterprise = defineRoute({
    ...Tenant,
    id: "identity.enterprise.close",
    method: "POST",
    path: "/v1/enterprise/closure",
    permission: "enterprise:close",
    marks: ["tenant", "step-up"],
    summary: "Close the enterprise: refused with the blocking orders, held funds, and cases while any remain; records are kept per the retention policy",
    prd: ["ENT-6"],
    request: {
        body: z.object({
            confirmLegalName: z.string().min(1).describe("The legal name typed again, as a safeguard"),
        }),
    },
    responses: { 204: { description: "Closed; every member loses access at once" } },
    errors: [
        "AUTH_REQUIRED",
        "TENANT_REQUIRED",
        "PERMISSION_DENIED",
        "STEP_UP_REQUIRED",
        "VALIDATION_FAILED",
        "CLOSURE_BLOCKED",
    ],
});
export const memberRoutes = [
    listMembers,
    updateMember,
    offboardMember,
    createInvitation,
    listInvitations,
    revokeInvitation,
    viewInvitation,
    acceptInvitation,
    listPermissions,
    listRoles,
    createRole,
    updateRole,
    deleteRole,
    closeEnterprise,
];
//# sourceMappingURL=member.routes.js.map